import { Guid } from "guid-typescript";
import { LocalStorageService } from "./local-storage.service"
import { LonglineGear } from "./longline-gear.model"
import { LookupDataService } from "./lookup-data.service"
import { PelagicGear } from "./pelagic-gear.model"
import { PotGear } from "./pot-gear.model"
import { LookupItem } from "./lookup-item.model";
import { GroundfishDelivery } from "./groundfish-delivery.model";
import { HMSDelivery } from "./hms-delivery.model";
import { TrawlDelivery } from "./trawl-delivery.model";

/**
 * The fish ticket number, buyer, and delivery date.
 */
export interface DeliverySubmission {
  FishTicketNumber: string,
  Buyer: string,
  DeliveryDate: string
};

/**
 * A submitted logbook consisting of fishing events with all catch and landing details
 */
export interface LandingSubmission {
  "logbookData": {
    LogbookType: string,
    Version: number,
    SubmissionDate: string,
    UserID: number,
    OperatorName: string,
    VesselNumber: string,
    VesselName: string,
    EFPTrip: boolean,
    EMTrip?: boolean,
    ObservedTrip: boolean,
    PermitTypeID: number,
    PermitNumber: string,
    ReturnDate: string,
    ReturnPortID: number,
    LocalLandingGUID: string,
    FishingEvents: EventSubmission[],
    Deliveries: DeliverySubmission[]
  }
};

export interface HMSSubmission {
  "logbookData": {
    LogbookType: string,
    Version: number,
    SubmissionDate: string,
    UserID: number,
    OperatorName: string,
    VesselNumber: string,
    VesselName: string,
    DepartureDate: string,
    DeparturePortName: string,
    DeparturePortID: number,
    ReturnDate: string,
    ReturnPortName: string,
    ReturnPortID: number,
    LocalLandingGUID: string,
    HMSUnloading: HMSUnloading[],
    HMSSet: HMSSetSubmission[]
  }
}

export interface TrawlSubmission {
  "logbookData": {
    LogbookType: string,
    UserID: number,
    Version: number,
    OperatorName: string,
    CrewSize: number,
    VesselNumber: string,
    VesselName: string,
    EFPTrip: boolean,
    EMTrip: boolean,
    ObservedTrip: boolean,
    AtSeaTrip?: boolean,
    MothershipName?: string,
    DepartureDate: string,
    DeparturePortID: number,
    ReturnDate: string,
    ReturnPortID: number,
    LocalLandingGUID: string,
    SubmissionDate: string,
    Comments: string,
    FishingEvents: TrawlSetSubmission[],
    Deliveries?: DeliverySubmission[]
  }

}

export interface HMSUnloading {
  HMSUnloadingPortName: string,
  HMSUnloadingPortID: number,
  UnloadingDate: string,
  HMSSpeciesName: string,
  HMSSpeciesID: number,
  PoundsLanded: number,
  LandingReceiptNumber: string,
}
/**
 * One for each fishing event attached to current delivery
 */
export interface EventSubmission {
  FishingEventGear: FishingEventGear,
  Catch: FishingEventCatch[],
  FishingStartDateTime: string,
  FishingStartLatitude: number,
  FishingStartLongitude: number,
  FishingStartAutoValues: boolean,
  FishingStartDepth?: number,
  FishingEndDateTime: string,
  FishingEndLatitude: number,
  FishingEndLongitude: number,
  FishingEndAutoValues: boolean,
  FishingEndDepth?: number,
  DepthOffBottom?: number,
  GearID: number,
  StringSetNumber: string,
  GearPerSet?: number,
  LostPotCount?: number,
  Bait: string,
  Comments: string
};

export interface TrawlSetSubmission {
  TargetStrategyID: number,
  NetTypeID: number,
  CodendCapacity?: number,
  FishingStartDateTime: string,
  FishingStartLatitude: number,
  FishingStartLongitude: number,
  FishingStartAutoValues: boolean,
  FishingEndDateTime: string,
  FishingEndLatitude: number,
  FishingEndLongitude: number,
  FishingEndAutoValues: boolean,
  AvgDepth: number,
  LostNet: boolean,
  Comments: string,
  Catch: FishingEventCatch[]

}

export interface HMSSetSubmission {
  HMSetID: number,
  FishingDate: string,
  FishingActivityTypeName: string,
  FishingActivityTypeID: number,
  Latitude: number,
  Longitude: number,
  BuoyCount?: number,
  HookCount?: number,
  HMSGearName: string,
  HMSGearID: number,
  AircraftUsed?: boolean,
  HoursFished: number,
  Comments: string,
  HMSCatch: HMSCatch[]
}

/**
 * Gear on each fishing event
 */
export interface FishingEventGear {
  PotTypeID: number,
  PotHeight: number,
  PotWidth: number,
  PotLength: number,
  Spacing: number,
  EscapementHoleSize: number,
  EscapementHoleCount: number,
  LonglineTypeID: number,
  LengthSkateLineTub: number,
  HookSize: number,
  HookSpacing: number,
  NumHooksSkateTub: number,
  TotalNumLostSkatesTubs: number,
  FloatingLongline: boolean,
  PelagicGearTypeID: number,
  TotalNumLinesRods: number,
  NumHooksPerLineRod: number,
  NTRCA: boolean
};

/**
 * Catch on every fishing event
 */
export interface FishingEventCatch {
  SpeciesID: number,
  CatchDisposition: string,
  Weight?: number,
  FishCount?: number
};

export interface HMSCatch {
  HMSCatchID: number,
  HMSSpeciesName: string,
  HMSSpeciesID: number,
  Disposition: string,
  FishCount: number,
  AvgWeight?: number
}

/**
 * Serialize the given landing, parsing all sets, catch, gear, and necessary landing details.
 * @param landing The landing to submit
 * @returns 
 */
export function ConvertToLogbook(dataSync: LocalStorageService, landing: GroundfishDelivery, currentSpecies?: LookupItem[]): LandingSubmission {
  if (!landing.localLandingGUID || landing.localLandingGUID == "") {
    landing.localLandingGUID = Guid.create().toString().toUpperCase();
  }


  //Handle edits

  let logbook: LandingSubmission = {
    "logbookData": {
      LogbookType: "Non-Trawl GF",
      Version: landing.logbookVersion,
      SubmissionDate: toIsoString(new Date()),
      UserID: dataSync.current_user().operatorUserID,
      OperatorName: landing.operatorName,
      VesselNumber: landing.vessel.vesselCoastNumber,
      VesselName: landing.vessel.vesselName,
      EFPTrip: landing.isEFP,
      EMTrip: landing.permitType.name == 'IFQ' ? landing.isEMTrip : undefined,
      ObservedTrip: landing.isObservedTrip,
      PermitTypeID: landing.permitType.id,
      PermitNumber: landing.permitNumber,
      ReturnDate: landing.returnDate ? toIsoString(new Date(landing.returnDate), false) : '',
      ReturnPortID: landing.returnPort.id,
      LocalLandingGUID: landing.localLandingGUID,
      FishingEvents: [],
      Deliveries: []
    }
  }

  if (landing.permitType.name != 'IFQ') delete logbook.logbookData.EMTrip;

  landing.tickets.forEach(tck => {
    logbook.logbookData.Deliveries.push({
      FishTicketNumber: tck.ticketNumber,
      Buyer: tck.buyer,
      DeliveryDate: tck.deliveryDate ? toIsoString(new Date(tck.deliveryDate), false) : ''
    });
  });

  landing.sets.forEach(set => {
    //For each set serialize catch data and gear
    let gear: FishingEventGear = {
      PotTypeID: 0,
      PotHeight: 0,
      PotWidth: 0,
      PotLength: 0,
      Spacing: 0,
      EscapementHoleSize: 0,
      EscapementHoleCount: 0,
      LonglineTypeID: 0,
      LengthSkateLineTub: 0,
      HookSize: 0,
      HookSpacing: 0,
      NumHooksSkateTub: 0,
      TotalNumLostSkatesTubs: 0,
      FloatingLongline: false,
      PelagicGearTypeID: 0,
      TotalNumLinesRods: 0,
      NumHooksPerLineRod: 0,
      NTRCA: false
    };

    let gearID = NaN;

    if (set.gear.type.toLowerCase() == "pot") {
      let setGear = set.gear as PotGear;
      gearID = LookupDataService.potID;
      gear.PotTypeID = setGear.gearSubtype.id;
      gear.PotHeight = setGear.potHeight;
      gear.PotLength = setGear.potLength;
      gear.PotWidth = setGear.potWidth;
      gear.Spacing = setGear.spacing;
      gear.EscapementHoleSize = setGear.escapeHoleSize;
      gear.EscapementHoleCount = setGear.escapeHoleCount;
    } else if (set.gear.type.toLowerCase() == "longline") {
      let setGear = set.gear as LonglineGear;
      gearID = LookupDataService.lineID;
      gear.LonglineTypeID = setGear.gearSubtype.id;
      gear.LengthSkateLineTub = setGear.skateLength;
      gear.HookSize = setGear.hookSize;
      gear.HookSpacing = setGear.hookSpacing;
      gear.NumHooksSkateTub = setGear.numHooksPerSkateTub;
      gear.TotalNumLostSkatesTubs = set.lostPotCount; //setGear.totalNumLostSkatesTubs;
      gear.FloatingLongline = setGear.isFloatingLongline;
    } else {
      let setGear = set.gear as PelagicGear;
      gearID = LookupDataService.pelID;
      gear.PelagicGearTypeID = set.gear.gearSubtype.id;
      gear.TotalNumLinesRods = setGear.totalNumLinesRods;
      gear.NumHooksPerLineRod = setGear.numHooksPerLineRod;
      gear.NTRCA = setGear.NT_RCA;
    }

    let event: EventSubmission = {
      FishingEventGear: gear,
      Catch: [],
      FishingStartDateTime: set.startDate ? toIsoString(new Date(set.startDate)) : '',
      FishingStartLatitude: set.startLat.decimal,
      FishingStartLongitude: set.startLon.decimal,
      FishingStartAutoValues: !set.startLatManualEntry && !set.startLonManualEntry,
      FishingStartDepth: set.gear.type != 'Pelagic' ? set.startDepth : undefined,
      FishingEndDateTime: set.endDate ? toIsoString(new Date(set.endDate)) : '',
      FishingEndLatitude: set.endLat.decimal,
      FishingEndLongitude: set.endLon.decimal,
      FishingEndAutoValues: !set.endLatManualEntry && !set.endLonManualEntry,
      FishingEndDepth: set.gear.type != 'Pelagic' ? set.endDepth : undefined,
      DepthOffBottom: set.gear.type == 'Pelagic' ? set.depthOffBottom : undefined,
      GearID: gearID,
      StringSetNumber: set.setNumber,
      GearPerSet: set.skateNum,
      LostPotCount: set.lostPotCount,
      Bait: set.baitType,
      Comments: set.comments
    };

    if (set.gear.type == 'Pelagic') {
      delete event.FishingStartDepth;
      delete event.FishingEndDepth;
      delete event.GearPerSet;
      delete event.LostPotCount;
    } else {
      delete event.DepthOffBottom;
    }

    set.catchItems.forEach(ct => {
      let correctedSp = currentSpecies?.find(cs => cs.name.trim().toLowerCase() == ct.species.name.trim().toLowerCase()) ?? ct.species;
      event.Catch.push({
        SpeciesID: correctedSp.id,
        CatchDisposition: 'Retained',
        Weight: ct.retainedWeight,
        FishCount: ct.retainedCount
      });
      event.Catch.push({
        SpeciesID: correctedSp.id,
        CatchDisposition: 'Released',
        Weight: ct.releasedWeight,
        FishCount: ct.releasedCount
      });
    });

    //Insert set into logbook
    logbook.logbookData.FishingEvents.push(event);
  });

  return logbook;
}

//Proper date strings
//Proper lookup items for all these lookups
export function ConvertHMSToLogbook(dataSync: LocalStorageService, landing: HMSDelivery): HMSSubmission {
  if (!landing.localLandingGUID || landing.localLandingGUID == "") {
    landing.localLandingGUID = Guid.create().toString().toUpperCase();
  }

  console.log(landing);
  let hmsLogbook: HMSSubmission = {
    "logbookData": {
      LogbookType: "HMS",
      Version: landing.logbookVersion,
      SubmissionDate: toIsoString(new Date()),
      UserID: dataSync.current_user().operatorUserID,
      OperatorName: landing.operatorName,
      VesselNumber: landing.vessel.vesselCoastNumber,
      VesselName: landing.vessel.vesselName,
      DepartureDate: new Date(landing.departureDate).toISOString().slice(0, 10),
      DeparturePortName: landing.departurePort.name,
      DeparturePortID: landing.departurePort.id,
      ReturnDate: new Date(landing.arrivalDate).toISOString().slice(0, 10),
      ReturnPortName: landing.arrivalPort.name,
      ReturnPortID: landing.arrivalPort.id,
      LocalLandingGUID: landing.localLandingGUID,
      HMSUnloading: [],
      HMSSet: []
    }
  };
  console.log("Set base logbook data...")
  landing.unloading.forEach(ul => {
    ul.species.forEach(sp => {
      const species = LookupDataService._hmsSetSpecies.find(s => s.name == sp.speciesName);
      if (species) {
        hmsLogbook.logbookData.HMSUnloading.push({
          HMSUnloadingPortName: ul.unloadingPort.name,
          HMSUnloadingPortID: ul.unloadingPort.id,
          UnloadingDate: new Date(ul.unloadingDate).toISOString().slice(0, 10),
          HMSSpeciesName: species.name,
          HMSSpeciesID: species.id,
          PoundsLanded: +sp.poundsLanded,
          LandingReceiptNumber: sp.landingReceipt
        });
      }
    });
  });
  console.log("Set unloading data...")

  console.log(landing.sets)
  landing.sets.forEach((set, i) => {
    let mode = LookupDataService._hmsFishingModes.find(fm => fm.name == set.fishingMode)!;
    let gear = set.gear.type == 'Deep Set Buoy Gear' ? LookupDataService._HmsDBSGTypes.find(g => g.name == set.gear.gearSubtype.name) : LookupDataService._HmsGearTypes.find(g => g.name == set.gear.type);

    hmsLogbook.logbookData.HMSSet.push({
      FishingDate: new Date(set.startDate).toISOString().slice(0, 10),
      FishingActivityTypeName: mode.name,
      FishingActivityTypeID: mode.id,
      BuoyCount: undefined,
      HookCount: undefined,
      Latitude: set.startLat.decimal,
      Longitude: set.startLon.decimal,
      HMSGearName: gear!.name,
      HMSGearID: gear!.id,
      AircraftUsed: undefined,
      HoursFished: +set.hoursFished,
      Comments: set.comments,
      HMSetID: i,
      HMSCatch: []
    });

    let ind = hmsLogbook.logbookData.HMSSet.length - 1;
    if (set.gear.type == 'Harpoon') hmsLogbook.logbookData.HMSSet[ind].AircraftUsed = !!set.aircraftUsed;
    else delete hmsLogbook.logbookData.HMSSet[ind].AircraftUsed

    if (set.gear.type == 'Deep Set Buoy Gear') {
      hmsLogbook.logbookData.HMSSet[ind].BuoyCount = +set.buoyCount;
      hmsLogbook.logbookData.HMSSet[ind].HookCount = +set.hookCount;
    } else {
      delete hmsLogbook.logbookData.HMSSet[ind].BuoyCount;
      delete hmsLogbook.logbookData.HMSSet[ind].HookCount;
    }

    set.catchItems.forEach(ci => {
      hmsLogbook.logbookData.HMSSet[ind].HMSCatch.push({
        HMSCatchID: i,
        HMSSpeciesName: ci.species.name,
        HMSSpeciesID: ci.species.id,
        Disposition: "Retained",
        FishCount: ci.retainedCount!,
        AvgWeight: ci.retainedWeight
      });
      hmsLogbook.logbookData.HMSSet[ind].HMSCatch.push({
        HMSCatchID: i,
        HMSSpeciesName: ci.species.name,
        HMSSpeciesID: ci.species.id,
        Disposition: "Released",
        FishCount: ci.releasedCount!,
        AvgWeight: ci.releasedWeight
      });
      if (set.gear.type != 'Harpoon') {
        delete hmsLogbook.logbookData.HMSSet[ind].HMSCatch[hmsLogbook.logbookData.HMSSet[ind].HMSCatch.length-1].AvgWeight;
        delete hmsLogbook.logbookData.HMSSet[ind].HMSCatch[hmsLogbook.logbookData.HMSSet[ind].HMSCatch.length-2].AvgWeight;
      }
    });
  });
  console.log("Set fishing event data...")

  return hmsLogbook;
}


export function ConvertTrawlToLogbook(dataSync: LocalStorageService, delivery: TrawlDelivery, currentSpecies?: LookupItem[]): TrawlSubmission {
  if (!delivery.localLandingGUID || delivery.localLandingGUID == "") {
    delivery.localLandingGUID = Guid.create().toString().toUpperCase();
  }

  let logbook: TrawlSubmission = {
    "logbookData": {
      LogbookType: "Trawl",
      Version: delivery.logbookVersion,
      SubmissionDate: toIsoString(new Date()),
      UserID: dataSync.current_user().operatorUserID,
      OperatorName: delivery.operatorName,
      CrewSize: delivery.crewSize,
      VesselNumber: delivery.vessel.vesselCoastNumber,
      VesselName: delivery.vessel.vesselName,
      EFPTrip: delivery.isEFP,
      EMTrip: delivery.isEMTrip,
      ObservedTrip: delivery.isObservedTrip,
      DepartureDate: delivery.departureDate ? toIsoString(new Date(delivery.departureDate)) : '',
      ReturnDate: delivery.returnDate ? toIsoString(new Date(delivery.returnDate)) : '',
      DeparturePortID: delivery.departurePort.id,
      AtSeaTrip: delivery.atSeaDelivery,
      MothershipName: delivery.mothershipName,
      ReturnPortID: delivery.returnPort.id,
      LocalLandingGUID: delivery.localLandingGUID,
      Comments: delivery.comments,
      FishingEvents: [],
      Deliveries: []
    }
  }

  if (!delivery.atSeaDelivery) {
    delete logbook.logbookData.MothershipName;
    delete logbook.logbookData.AtSeaTrip;
    logbook.logbookData.Deliveries = delivery.tickets.map(tck => {
      return {
        FishTicketNumber: tck.ticketNumber,
        Buyer: tck.buyer,
        DeliveryDate: tck.deliveryDate ? toIsoString(new Date(tck.deliveryDate), false) : ''
      }
    });
    
  } else {
    delete logbook.logbookData.Deliveries;
  }

  delivery.sets.forEach(set => {
    let event: TrawlSetSubmission = {
      TargetStrategyID: set.targetSpecies.id,
      NetTypeID: set.netType.id,
      CodendCapacity: set.codendCapacity,
      FishingStartDateTime: set.startDate ? toIsoString(new Date(set.startDate)) : '',
      FishingStartLatitude: set.startLat.decimal,
      FishingStartLongitude: set.startLon.decimal,
      FishingStartAutoValues: !set.startLatManualEntry && !set.startLonManualEntry,
      FishingEndDateTime: set.endDate ? toIsoString(new Date(set.endDate)) : '',
      FishingEndLatitude: set.endLat.decimal,
      FishingEndLongitude: set.endLon.decimal,
      FishingEndAutoValues: !set.endLatManualEntry && !set.endLonManualEntry,
      AvgDepth: set.averageDepth,
      LostNet: set.lostNet,
      Comments: set.comments,
      Catch: []
    };

    if (set.codendCapacity == null || set.codendCapacity == undefined) delete event.CodendCapacity;

    //Adjust for possible null values
    set.catchItems.forEach(ct => {
      let correctedSp = currentSpecies?.find(cs => cs.name.trim().toLowerCase() == ct.species.name.trim().toLowerCase()) ?? ct.species;
      event.Catch.push({
        SpeciesID: correctedSp.id,
        CatchDisposition: 'R',
        Weight: ct.retainedWeight,
        FishCount: ct.retainedCount
      });

      //Don't add discards if null values
      if (!ct.releasedWeight || !ct.releasedCount) {
        event.Catch.push({
          SpeciesID: correctedSp.id,
          CatchDisposition: 'D',
          Weight: ct.releasedWeight,
          FishCount: ct.releasedCount
        });
      }
      
    });

    logbook.logbookData.FishingEvents.push(event);
  });
  
  return logbook;
}

export function toIsoString(date: Date, includeTime = true) {
  var tzo = -date.getTimezoneOffset(),
    dif = tzo >= 0 ? '+' : '-',
    pad = function (num: number) {
      return (num < 10 ? '0' : '') + num;
    };

  var timePortion = pad(date.getHours()) +
    ':' + pad(date.getMinutes()) +
    ':' + pad(date.getSeconds()) +
    dif + pad(Math.floor(Math.abs(tzo) / 60)) +
    ':' + pad(Math.abs(tzo) % 60);

  return date.getFullYear() +
    '-' + pad(date.getMonth() + 1) +
    '-' + pad(date.getDate()) +
    'T' + (includeTime ? timePortion : '00:00:00Z')
}
